// Supplier-status banner (flag REACT_APP_SUPPLIER_STATUS_ENABLED, 14/9): the
// pure decisions - when the status route is read at all, how often, and
// which degraded episode a dismissal belongs to. Pure module: no flag reads
// and no React here, so enviroment.ts can import the parser without a cycle
// and every rule is unit-testable without react-query, the router or the
// DOM. The module store (route-missing memory, re-check token) and the
// flag-gated helpers live in supplierStatus.ts.
//
// Contract B (GET supplier-status): 0 supplier calls on the API side (it
// reads its health latch + the last App117Probe result from cache and caches
// its own answer ≥ 30 s). Contract C (call budget on the FE): 1 GET after
// the profile has loaded, then a poll ONLY while a logo/design editor or the
// logo list is mounted OR the status is degraded, none while the tab is
// hidden, none on other pages, and one immediate re-check after a 503
// supplierUnavailable.

export const SUPPLIER_STATUS_QUERY_KEY = ["supplier-status"] as const;

export const DEFAULT_SUPPLIER_STATUS_POLL_MS = 60_000;
// Guard rails for REACT_APP_SUPPLIER_STATUS_POLL_MS: a typo must never turn
// the poll into a hammer (the API caches its answer 30 s anyway) nor stall
// it for hours.
export const MIN_SUPPLIER_STATUS_POLL_MS = 15_000;
export const MAX_SUPPLIER_STATUS_POLL_MS = 600_000;

/**
 * The poll interval in ms: unset, empty, not a number, zero or negative =
 * the default 60 000; positive values are rounded and clamped to
 * [15 000, 600 000].
 */
export function parseSupplierStatusPollMs(raw: string | undefined): number {
  if (raw === undefined) return DEFAULT_SUPPLIER_STATUS_POLL_MS;
  const trimmed = raw.trim();
  if (trimmed === "") return DEFAULT_SUPPLIER_STATUS_POLL_MS;
  const value = Number(trimmed);
  if (!Number.isFinite(value) || value <= 0) {
    return DEFAULT_SUPPLIER_STATUS_POLL_MS;
  }
  return Math.min(
    MAX_SUPPLIER_STATUS_POLL_MS,
    Math.max(MIN_SUPPLIER_STATUS_POLL_MS, Math.round(value))
  );
}

// The routes on which the supplier's five interactive mutations (upload,
// clone, colour, method, resize) can be started: the logo list (/logo - its
// popup clones/uploads), the standalone logo editor (/logo/create) and the
// design editor (/design/:designId/editor, whose logo tool recolours,
// resizes and changes the method). Only there is the status polled while
// it is ok. The customer edit screen's logo section and the customer-list
// dialog are not in scope (documented limit: the 503 re-check still covers
// them, and a degraded status polls everywhere).
const SCOPE_PATHS: readonly RegExp[] = [
  /^\/logo$/,
  /^\/logo\/create$/,
  /^\/design\/[^/]+\/editor$/,
];

/** True for a pathname on which the status is polled while ok. */
export function isSupplierStatusScopePath(pathname: string | undefined): boolean {
  if (typeof pathname !== "string") return false;
  const normalized = pathname.trim().toLowerCase().replace(/\/+$/, "") || "/";
  return SCOPE_PATHS.some((pattern) => pattern.test(normalized));
}

export interface SupplierStatusPollInput {
  /** The route answered 404 (API flag off) within the last SUPPLIER_STATUS_ROUTE_MISSING_TTL_MS. */
  routeMissing: boolean;
  /** document.visibilityState === "hidden". */
  hidden: boolean;
  /** The current route is one of the scope paths. */
  inScope: boolean;
  /** The last status read says degraded. */
  degraded: boolean;
  /** A status has been read at least once (react-query has data). */
  hasData: boolean;
  /** The last read failed and no data exists (react-query holds the error): settled, not "unknown". */
  hasError: boolean;
  /** The configured poll interval. */
  pollMs: number;
}

export interface SupplierStatusPollPlan {
  /** react-query `enabled`: false = neither the first read nor any refetch. */
  enabled: boolean;
  /** react-query `refetchInterval`: false = no timer. */
  refetchInterval: number | false;
}

/**
 * The react-query plan for the status query. Never anything while the route
 * is remembered as missing (404 within the TTL), never while the tab is
 * hidden. Otherwise the query is live for the first read (nothing settled
 * yet: neither data nor an error), on a scope route and while degraded -
 * and polls on the interval only on a scope route or while degraded. Out of
 * scope with an ok status OR a failed first read the query is disabled:
 * 0 calls - not even when the tab is hidden and shown again - until the
 * user enters a scope route (react-query then refetches only if the last
 * read is older than the interval, its staleTime; a held error is always
 * stale) or a 503 re-check asks for a read.
 */
export function planSupplierStatusPoll(
  input: SupplierStatusPollInput
): SupplierStatusPollPlan {
  if (input.routeMissing || input.hidden) {
    return { enabled: false, refetchInterval: false };
  }
  const polling = input.inScope || input.degraded;
  const settled = input.hasData || input.hasError;
  return {
    enabled: polling || !settled,
    refetchInterval: polling ? input.pollMs : false,
  };
}

/**
 * The identity of a degraded episode for the per-session dismissal: the
 * reason and the API's `since`. A new episode (new `since`, or a different
 * reason) has a new key, so a dismissed banner comes back on the next state
 * change. Known limit: with `since` null, episodes with the same reason share
 * a key for the rest of the session.
 *
 * Contract note for the API (GET supplier-status): `since` is the START of
 * the episode - the latch's OpenedAtUtc / the first failed probe - and stays
 * unchanged until the status is ok again. It must never be lastProbeUtc:
 * every 5-minute probe would then mint a new key and bring a dismissed
 * banner back at the next poll.
 */
export function supplierStatusEpisodeKey(status: {
  reason: string | null;
  since: Date | null;
}): string {
  const since =
    status.since instanceof Date && !Number.isNaN(status.since.getTime())
      ? status.since.toISOString()
      : "-";
  return `${status.reason ?? "-"}|${since}`;
}

/**
 * The relative age of the episode for the banner ("just now" / minutes /
 * hours). Null when `since` is unknown or in the future. Pure; `now`
 * injectable for tests.
 */
export function supplierStatusSinceAge(
  since: Date | null,
  now: number = Date.now()
): { unit: "justNow" | "minutes" | "hours"; value: number } | null {
  if (!(since instanceof Date) || Number.isNaN(since.getTime())) return null;
  const elapsedMs = now - since.getTime();
  if (elapsedMs < 0) return null;
  const minutes = Math.floor(elapsedMs / 60_000);
  if (minutes < 1) return { unit: "justNow", value: 0 };
  if (minutes < 60) return { unit: "minutes", value: minutes };
  return { unit: "hours", value: Math.floor(minutes / 60) };
}
